// src/modules/accounting/pages/DoubleEntryDashboard.jsx

import React, { useEffect, useState, useCallback } from 'react';
import {
  Row, Col, Card, Statistic, Tag, Spin, Alert, Typography, Divider, Space,
  DatePicker, Button, Tooltip, Progress, Empty
} from 'antd';
import {
  CheckCircleOutlined, WarningOutlined, ReloadOutlined,
  ArrowUpOutlined, ArrowDownOutlined, DollarOutlined,
  BankOutlined, BookOutlined, BarChartOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getEnhancedDashboard } from '../api';
import DataTable from '../../../components/UI/DataTable';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export default function DoubleEntryDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.periodStart = dateRange[0].startOf('day').toISOString();
        params.periodEnd = dateRange[1].endOf('day').toISOString();
        params.asOfDate = dateRange[1].endOf('day').toISOString();
      }
      const res = await getEnhancedDashboard(params);
      setData(res.data || res);
    } catch (err) {
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '80px auto' }} />;
  if (error) return <Alert type="error" message="Dashboard Error" description={error} showIcon />;
  if (!data) return <Empty description="No data" />;

  const { systemHealth, financialSummary, cashPosition, accountBalances,
    recentJournals, sourceActivity, monthlyTrend, journalCounts } = data;

  const healthColor = systemHealth?.status === 'HEALTHY' ? '#52c41a' : '#faad14';
  const healthIcon = systemHealth?.status === 'HEALTHY'
    ? <CheckCircleOutlined style={{ color: healthColor }} />
    : <WarningOutlined style={{ color: healthColor }} />;

  const fmt = (v) => Number((v || 0).toFixed(2)).toLocaleString('en-IN');
  const pct = (v) => v != null ? `${Number(v).toFixed(1)}%` : '—';

  return (
    <div>
      {/* ── Filter Bar ── */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}>Double-Entry Accounting Dashboard</Title>
        </Col>
        <Col>
          <Space>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              allowClear
              format="DD/MM/YYYY"
            />
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={fetchDashboard} />
            </Tooltip>
          </Space>
        </Col>
      </Row>

      {/* ── 1. System Health ── */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="System Status"
              value={systemHealth?.status || 'UNKNOWN'}
              prefix={healthIcon}
              valueStyle={{ color: healthColor, fontSize: 18 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="Trial Balance"
              value={systemHealth?.trialBalance?.isBalanced ? 'Balanced' : 'Imbalanced'}
              valueStyle={{ color: systemHealth?.trialBalance?.isBalanced ? '#52c41a' : '#ff4d4f', fontSize: 16 }}
              suffix={systemHealth?.trialBalance?.difference > 0 ? `(₹${fmt(systemHealth.trialBalance.difference)} off)` : ''}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="Total Debit"
              value={fmt(systemHealth?.trialBalance?.totalDebit)}
              prefix="₹"
              valueStyle={{ fontSize: 16 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small">
            <Statistic
              title="Total Credit"
              value={fmt(systemHealth?.trialBalance?.totalCredit)}
              prefix="₹"
              valueStyle={{ fontSize: 16 }}
            />
          </Card>
        </Col>
      </Row>

      {/* ── Accounting Equation ── */}
      {systemHealth?.accountingEquation && (
        <Card size="small" style={{ marginTop: 16, background: '#fafafa' }}>
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <Text strong>Accounting Equation: </Text>
              <Text code>{systemHealth.accountingEquation.equation}</Text>
            </Col>
            <Col>
              <Tag color={systemHealth.accountingEquation.isBalanced ? 'green' : 'red'}>
                {systemHealth.accountingEquation.isBalanced ? 'BALANCED' : 'IMBALANCED'}
              </Tag>
            </Col>
          </Row>
        </Card>
      )}

      <Divider />

      {/* ── 2. Financial Summary KPIs ── */}
      <Title level={5}><DollarOutlined /> Profit & Loss Summary</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={4}>
          <Card size="small">
            <Statistic title="Total Revenue" value={fmt(financialSummary?.profitLoss?.totalRevenue)} prefix="₹" valueStyle={{ fontSize: 16, color: '#3f8600' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card size="small">
            <Statistic title="COGS" value={fmt(financialSummary?.profitLoss?.cogs)} prefix="₹" valueStyle={{ fontSize: 16 }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card size="small">
            <Statistic title="Gross Profit" value={fmt(financialSummary?.profitLoss?.grossProfit)} prefix="₹"
              valueStyle={{ fontSize: 16, color: financialSummary?.profitLoss?.grossProfit >= 0 ? '#3f8600' : '#cf1322' }}
              suffix={<Text type="secondary" style={{ fontSize: 12 }}>{pct(financialSummary?.profitLoss?.grossProfitMargin)}</Text>}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card size="small">
            <Statistic title="Operating Expenses" value={fmt(financialSummary?.profitLoss?.operatingExpenses)} prefix="₹" valueStyle={{ fontSize: 16, color: '#cf1322' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card size="small">
            <Statistic title="Operating Profit" value={fmt(financialSummary?.profitLoss?.operatingProfit)} prefix="₹"
              valueStyle={{ fontSize: 16, color: financialSummary?.profitLoss?.operatingProfit >= 0 ? '#3f8600' : '#cf1322' }}
              suffix={<Text type="secondary" style={{ fontSize: 12 }}>{pct(financialSummary?.profitLoss?.operatingProfitMargin)}</Text>}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card size="small">
            <Statistic title="Net Profit" value={fmt(financialSummary?.profitLoss?.netProfit)}
              valueStyle={{ fontSize: 18, fontWeight: 'bold', color: financialSummary?.profitLoss?.netProfit >= 0 ? '#3f8600' : '#cf1322' }}
              prefix={financialSummary?.profitLoss?.netProfit >= 0 ? <><ArrowUpOutlined /> ₹</> : <><ArrowDownOutlined /> ₹</>}
              suffix={<Text type="secondary" style={{ fontSize: 12 }}>{pct(financialSummary?.profitLoss?.netProfitMargin)}</Text>}
            />
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* ── 3. Balance Sheet KPIs ── */}
      <Title level={5}><BankOutlined /> Balance Sheet Summary</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8} lg={4}>
          <Card size="small"><Statistic title="Total Assets" value={fmt(financialSummary?.balanceSheet?.totalAssets)} prefix="₹" valueStyle={{ fontSize: 16 }} /></Card>
        </Col>
        <Col xs={24} sm={8} lg={4}>
          <Card size="small"><Statistic title="Current Assets" value={fmt(financialSummary?.balanceSheet?.currentAssets)} prefix="₹" valueStyle={{ fontSize: 14 }} /></Card>
        </Col>
        <Col xs={24} sm={8} lg={4}>
          <Card size="small"><Statistic title="Total Liabilities" value={fmt(financialSummary?.balanceSheet?.totalLiabilities)} prefix="₹" valueStyle={{ fontSize: 16 }} /></Card>
        </Col>
        <Col xs={24} sm={8} lg={4}>
          <Card size="small"><Statistic title="Total Equity" value={fmt(financialSummary?.balanceSheet?.totalEquity)} prefix="₹" valueStyle={{ fontSize: 16 }} /></Card>
        </Col>
        <Col xs={24} sm={8} lg={4}>
          <Card size="small"><Statistic title="Working Capital" value={fmt(financialSummary?.balanceSheet?.workingCapital)} prefix="₹" valueStyle={{ fontSize: 14, color: financialSummary?.balanceSheet?.workingCapital >= 0 ? '#3f8600' : '#cf1322' }} /></Card>
        </Col>
        <Col xs={24} sm={8} lg={4}>
          <Card size="small"><Statistic title="Current Ratio" value={financialSummary?.balanceSheet?.currentRatio?.toFixed(2) || '—'} valueStyle={{ fontSize: 16 }} /></Card>
        </Col>
      </Row>

      <Divider />

      {/* ── 4. Cash Position ── */}
      <Title level={5}><DollarOutlined /> Cash Position</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic title="Total Cash & Bank" value={fmt(cashPosition?.total)} prefix="₹" valueStyle={{ fontSize: 20, fontWeight: 'bold', color: '#1890ff' }} />
          </Card>
        </Col>
        {cashPosition?.accounts?.map((acc) => (
          <Col xs={24} sm={8} key={acc.code}>
            <Card size="small">
              <Statistic title={`${acc.code} — ${acc.name}`} value={fmt(acc.balance)} prefix="₹" valueStyle={{ fontSize: 16 }} />
            </Card>
          </Col>
        ))}
      </Row>

      <Divider />

      {/* ── 5. Journal Entry Stats ── */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title={<><BookOutlined /> Journal Entry Summary</>} size="small">
            <Row gutter={16}>
              <Col span={6}><Statistic title="Total" value={journalCounts?.total || 0} /></Col>
              <Col span={6}><Statistic title="Posted" value={journalCounts?.POSTED || 0} valueStyle={{ color: '#52c41a' }} /></Col>
              <Col span={6}><Statistic title="Draft" value={journalCounts?.DRAFT || 0} valueStyle={{ color: '#faad14' }} /></Col>
              <Col span={6}><Statistic title="Reversed" value={journalCounts?.REVERSED || 0} valueStyle={{ color: '#ff4d4f' }} /></Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={<><BarChartOutlined /> Activity by Source Module</>} size="small">
            {sourceActivity && sourceActivity.length > 0 ? (
              <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                {sourceActivity.map((s) => (
                  <div key={s.source} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <Tag color="blue">{s.source}</Tag>
                    <Space>
                      <Text type="secondary">{s.count} entries</Text>
                      <Text strong>₹{fmt(s.totalValue)}</Text>
                    </Space>
                  </div>
                ))}
              </div>
            ) : <Empty description="No activity" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* ── 6. Account Balances by Type ── */}
      <Title level={5}>Account Balances by Type</Title>
      <Row gutter={[16, 16]}>
        {['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'].map((type) => {
          const typeColors = { ASSET: '#1890ff', LIABILITY: '#ff4d4f', EQUITY: '#722ed1', REVENUE: '#52c41a', EXPENSE: '#fa8c16' };
          const accts = accountBalances?.groups?.[type] || [];
          return (
            <Col xs={24} lg={12} xl={8} key={type}>
              <Card
                title={<><Tag color={typeColors[type]}>{type}</Tag> <Text>₹{fmt(accountBalances?.totals?.[type])}</Text></>}
                size="small"
                style={{ marginBottom: 8 }}
              >
                {accts.length > 0 ? (
                  <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                    {accts.map((a) => (
                      <div key={a.code} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid #f5f5f5' }}>
                        <Text style={{ fontSize: 12 }}>{a.code} — {a.name}</Text>
                        <Text strong style={{ fontSize: 12 }}>₹{fmt(a.balance)}</Text>
                      </div>
                    ))}
                  </div>
                ) : <Text type="secondary" style={{ fontSize: 12 }}>No balances</Text>}
              </Card>
            </Col>
          );
        })}
      </Row>

      <Divider />

      {/* ── 7. Monthly Trend ── */}
      {monthlyTrend && monthlyTrend.length > 0 && (
        <>
          <Title level={5}>Monthly Revenue vs Expenses</Title>
          <DataTable persistId="accounting.double-entry.monthly"
            dataSource={monthlyTrend.map((m, i) => ({ ...m, key: i }))}
            columns={[
              { title: 'Month', dataIndex: 'month', key: 'month' },
              { title: 'Revenue', dataIndex: 'revenue', key: 'revenue', render: (v) => `₹${fmt(v)}`, align: 'right' },
              { title: 'Expenses', dataIndex: 'expenses', key: 'expenses', render: (v) => `₹${fmt(v)}`, align: 'right' },
              { title: 'Net', dataIndex: 'netProfit', key: 'netProfit', render: (v) => <Text style={{ color: v >= 0 ? '#3f8600' : '#cf1322' }}>₹{fmt(v)}</Text>, align: 'right' },
              { title: 'Margin', dataIndex: 'netProfitMargin', key: 'margin', render: (v) => pct(v), align: 'right' },
            ]}
            pagination={false}
            size="small"
          />
          <Divider />
        </>
      )}

      {/* ── 8. Recent Journal Entries ── */}
      <Title level={5}>Recent Journal Entries</Title>
      <DataTable persistId="accounting.double-entry.recent"
        dataSource={(recentJournals || []).map((j, i) => ({ ...j, key: i }))}
        columns={[
          { title: 'Entry ID', dataIndex: 'entryId', key: 'entryId', width: 120 },
          { title: 'Date', dataIndex: 'date', key: 'date', render: (d) => d ? dayjs(d).format('DD/MM/YYYY') : '—', width: 100 },
          { title: 'Source', dataIndex: 'source', key: 'source', render: (s) => <Tag>{s}</Tag>, width: 160 },
          { title: 'Narration', dataIndex: 'narration', key: 'narration', ellipsis: true },
          { title: 'Debit', dataIndex: 'totalDebit', key: 'debit', render: (v) => `₹${fmt(v)}`, align: 'right', width: 120 },
          { title: 'Credit', dataIndex: 'totalCredit', key: 'credit', render: (v) => `₹${fmt(v)}`, align: 'right', width: 120 },
        ]}
        pagination={false}
        size="small"
        expandable={{
          expandedRowRender: (record) => (
            <DataTable persistId="accounting.double-entry.recent-lines" showToolbar={false}
              dataSource={(record.lines || []).map((l, i) => ({ ...l, key: i }))}
              columns={[
                { title: 'Account', key: 'account', render: (_, l) => l.account ? `${l.account.code} — ${l.account.name}` : '—' },
                { title: 'Debit', dataIndex: 'debit', render: (v) => v > 0 ? `₹${fmt(v)}` : '', align: 'right' },
                { title: 'Credit', dataIndex: 'credit', render: (v) => v > 0 ? `₹${fmt(v)}` : '', align: 'right' },
              ]}
              pagination={false}
              size="small"
              showHeader={false}
            />
          ),
        }}
      />
    </div>
  );
}
